/**
 * Shared Firestore access helpers used throughout config-dashboard.
 */

//TODO : break the file into smaller chunks
import {
  getFirebaseApp,
  getFirestoreDB,
} from "@risalabs_frontend_org/auth-wrapper";
import { Analytics, getAnalytics } from "firebase/analytics";
import {
  addDoc,
  arrayRemove,
  arrayUnion,
  collection,
  deleteDoc,
  doc,
  DocumentData,
  Firestore,
  FirestoreError,
  getCountFromServer,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  query,
  QueryCompositeFilterConstraint,
  QueryConstraint,
  QueryDocumentSnapshot,
  runTransaction,
  setDoc,
  startAfter,
  Timestamp,
  updateDoc,
  writeBatch,
} from "firebase/firestore";
import {
  deleteObject,
  getDownloadURL,
  getStorage,
  ref,
} from "firebase/storage";
import { Checklist } from "risa-data-model";
import { CmmEvent } from "../../../medonc/data-model/cmmEvent";
import { MedicalPaOrder } from "../../../medonc/data-model/medicalPaOrdersModel";
import { MedicalQuestion } from "../../../medonc/data-model/medicalQuestion";
import { WritebackQcRecord } from "../../../medonc/data-model/templateValidatorModel";
import { MemberModel } from "../../../medonc/pages/settings/members/add-members/add-members";
import { logDataToConsole, logError } from "../../../medonc/utils/customLogger";
import {
  FirestoreCollectionReference,
  FirestoreDocumentReference,
} from "./references";

function getDB(): Firestore {
  return getFirestoreDB();
}

let _firebaseAnalytics: Analytics | null = null;
export function getFirebaseAnalytics(): Analytics | null {
  if (_firebaseAnalytics) return _firebaseAnalytics;
  try {
    _firebaseAnalytics = getAnalytics(getFirebaseApp());
  } catch (error) {
    console.warn("Firebase Analytics not available:", error);
  }
  return _firebaseAnalytics;
}

function docToData<T>(doc: QueryDocumentSnapshot<DocumentData>): T {
  const data = doc.data() as DocumentData;

  for (const [key, value] of Object.entries(data)) {
    if (value instanceof Timestamp) {
      data[key] = value.toDate();
    } else if (typeof value === "object" && value !== null) {
      data[key] = convertTimestamps(value);
    }
  }
  return { id: doc.id, ...data } as T;
}

function convertTimestamps(obj: any): any {
  for (const [key, value] of Object.entries(obj)) {
    if (value instanceof Timestamp) {
      obj[key] = value.toDate();
    } else if (typeof value === "object" && value !== null) {
      obj[key] = convertTimestamps(value);
    }
  }
  return obj;
}

export class FirestoreService {
  static async getDocument<T>(documentPath: string): Promise<T> {
    try {
      const docRef = doc(getDB(), documentPath);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        return docToData<T>(docSnap);
      } else {
        throw new Error("Document does not exist!");
      }
    } catch (error) {
      logError(error as Error, "Error fetching document");
      throw error;
    }
  }

  static getFirestore() {
    return getDB();
  }

  /**
   * Subscribes to a bounded page of collection documents with optional cursor continuation.
   *
   * @param collectionPath - Firestore collection path
   * @param pageSize - Maximum rows to return to the consumer when `overFetchOneForHasNext` is used
   *   (otherwise both query limit and returned row count)
   * @param onData - Emits mapped rows, cursor for `startAfter` on the next page, and when over-fetching,
   *   `hasNextPage` derived from an extra document that is not included in `docs`
   * @param onError - Optional snapshot error handler
   * @param lastDoc - Document to start after (previous page end); omit for the first page
   * @param extraConstraints - e.g. `orderBy` / `where`
   * @param paginationOptions - When `overFetchOneForHasNext` is true, queries `pageSize + 1` documents,
   *   returns only the first `pageSize` as `docs`, sets `hasNextPage` if a peek row exists, and uses
   *   the last *displayed* snapshot doc as `lastVisible` so the next page does not skip a row
   * @returns Unsubscribe function for the snapshot listener
   */
  static paginateQueryRealtime<T extends DocumentData>(
    collectionPath: string,
    pageSize: number,
    onData: (data: {
      docs: T[];
      lastVisible: QueryDocumentSnapshot | null;
      hasNextPage?: boolean;
    }) => void,
    onError?: (error: FirestoreError) => void,
    lastDoc?: QueryDocumentSnapshot,
    extraConstraints: QueryConstraint[] = [],
    paginationOptions?: { overFetchOneForHasNext?: boolean },
  ): () => void {
    try {
      const overFetch = paginationOptions?.overFetchOneForHasNext === true;
      const queryLimit = overFetch ? pageSize + 1 : pageSize;
      const constraints: QueryConstraint[] = [
        ...extraConstraints,
        ...(lastDoc ? [startAfter(lastDoc)] : []),
        limit(queryLimit),
      ];

      const q = query(collection(getDB(), collectionPath), ...constraints);

      const unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          const snapDocs = snapshot.docs;
          if (overFetch) {
            const hasNextPage = snapDocs.length > pageSize;
            const visibleSnapDocs = snapDocs.slice(0, pageSize);
            const docs = visibleSnapDocs.map((d) => docToData<T>(d));
            const lastVisible =
              visibleSnapDocs.length > 0
                ? visibleSnapDocs[visibleSnapDocs.length - 1]!
                : null;
            onData({ docs, lastVisible, hasNextPage });
            return;
          }
          const docs = snapDocs.map((doc) => docToData<T>(doc));
          const lastVisible = snapDocs[snapDocs.length - 1] ?? null;
          onData({ docs, lastVisible });
        },
        (error) => {
          if (onError) {
            onError(error);
          } else {
            logError(error as Error, "Error in paginated query");
          }
        },
      );

      return unsubscribe;
    } catch (error) {
      logError(error as Error, "Error setting up paginated query");
      throw error;
    }
  }

  /**
   * Runs a one-shot Firestore query with the given constraints and maps documents to typed rows.
   *
   * @param collectionPath - Full collection path (same format as other FirestoreService helpers)
   * @param constraints - Firestore query constraints (where, orderBy, limit, etc.)
   * @returns Typed document payloads including document ids
   * @throws If Firestore rejects the query (for example missing composite index)
   */
  static async getDocumentsWithConstraints<T extends DocumentData>(
    collectionPath: string,
    constraints: QueryConstraint[],
  ): Promise<T[]> {
    try {
      const q = query(collection(getDB(), collectionPath), ...constraints);
      const snapshot = await getDocs(q);
      return snapshot.docs.map((docSnap) => docToData<T>(docSnap));
    } catch (error) {
      logError(error as Error, "Error in getDocumentsWithConstraints");
      throw error;
    }
  }

  static async paginateQuery<T extends DocumentData>(
    collectionPath: string,
    pageSize: number,
    onData: (data: {
      docs: T[];
      lastVisible: QueryDocumentSnapshot | null;
    }) => void,
    onError?: (error: FirestoreError) => void,
    lastDoc?: QueryDocumentSnapshot,
    extraConstraints: QueryConstraint[] = [],
  ): Promise<void> {
    try {
      const constraints: QueryConstraint[] = [
        ...extraConstraints,
        ...(lastDoc ? [startAfter(lastDoc)] : []),
        limit(pageSize),
      ];

      const q = query(collection(getDB(), collectionPath), ...constraints);

      const snapshot = await getDocs(q);
      const docs = snapshot.docs.map((doc) => docToData<T>(doc));
      const lastVisible = snapshot.docs[snapshot.docs.length - 1] ?? null;
      onData({ docs, lastVisible });
    } catch (error) {
      if (onError) {
        onError(error as FirestoreError);
      } else {
        logError(error as Error, "Error in paginated query");
      }
    }
  }

  static async paginateQueryWithCompositeFilter<T extends DocumentData>(
    collectionPath: string,
    pageSize: number,
    onData: (data: {
      docs: T[];
      lastVisible: QueryDocumentSnapshot | null;
    }) => void,
    onError?: (error: FirestoreError) => void,
    lastDoc?: QueryDocumentSnapshot,
    compositeFilter?: QueryCompositeFilterConstraint,
    extraConstraint?: QueryConstraint | QueryConstraint[],
  ): Promise<void> {
    try {
      const constraints: QueryConstraint[] = [
        ...(compositeFilter
          ? [compositeFilter as unknown as QueryConstraint]
          : []),
        ...(extraConstraint
          ? Array.isArray(extraConstraint)
            ? extraConstraint
            : [extraConstraint]
          : []),
        ...(lastDoc ? [startAfter(lastDoc)] : []),
        limit(pageSize),
      ];

      const q = query(collection(getDB(), collectionPath), ...constraints);

      const snapshot = await getDocs(q);
      const docs = snapshot.docs.map((doc) => docToData<T>(doc));
      const lastVisible = snapshot.docs[snapshot.docs.length - 1] ?? null;
      onData({ docs, lastVisible });
    } catch (error) {
      if (onError) {
        onError(error as FirestoreError);
      } else {
        logError(
          error as Error,
          "Error in paginated query with composite filter",
        );
      }
    }
  }

  static async getDocumentsByQuery<T>(
    collectionPath: string,
    queryConstraints: QueryConstraint[],
  ): Promise<T[]> {
    try {
      const q = query(collection(getDB(), collectionPath), ...queryConstraints);
      const querySnapshot = await getDocs(q);
      let documents: T[] = [];
      querySnapshot.forEach((doc) => {
        documents.push(docToData<T>(doc));
      });
      return documents;
    } catch (error) {
      logError(error as Error, "Error fetching documents with query");
      throw error;
    }
  }

  static async getAllDocuments<T>(collectionPath: string): Promise<T[]> {
    try {
      const querySnapshot = await getDocs(collection(getDB(), collectionPath));
      let documents: T[] = [];
      querySnapshot.forEach((doc) => {
        documents.push(docToData<T>(doc));
      });
      return documents;
    } catch (error) {
      logError(error as Error, "Error fetching documents");
      throw error;
    }
  }

  static async fetchQueryCollectionByCompositeFilter<T>(
    collectionPath: string,
    compositeFilter: QueryCompositeFilterConstraint,
    extraConstraints: QueryConstraint[] = [],
  ): Promise<T[]> {
    try {
      const constraints: QueryConstraint[] = [
        compositeFilter as unknown as QueryConstraint,
        ...extraConstraints,
      ];
      const q = query(collection(getDB(), collectionPath), ...constraints);
      const querySnapshot = await getDocs(q);
      let documents: T[] = [];
      querySnapshot.forEach((doc) => {
        documents.push(docToData<T>(doc));
      });
      return documents;
    } catch (error) {
      logError(
        error as Error,
        "Error fetching documents for fetchQueryCollection with composite filter",
      );
      throw error;
    }
  }

  static async getDocumentsByCompositeFilter<T>(
    collectionPath: string,
    compositeFilter: QueryCompositeFilterConstraint,
  ): Promise<T[]> {
    try {
      const q = query(
        collection(getDB(), collectionPath),
        compositeFilter as unknown as QueryConstraint,
      );
      const querySnapshot = await getDocs(q);
      let documents: T[] = [];
      querySnapshot.forEach((doc) => {
        documents.push(docToData<T>(doc));
      });
      return documents;
    } catch (error) {
      logError(
        error as Error,
        "Error fetching documents with composite filter",
      );
      throw error;
    }
  }

  static listenToAllDocuments<T>(
    collectionPath: string,
    onDocumentsReceived: (documents: T[]) => void,
    onError?: (error: FirestoreError) => void,
  ): () => void {
    const unsubscribe = onSnapshot(
      collection(getDB(), collectionPath),
      (querySnapshot) => {
        let documents: T[] = [];
        querySnapshot.forEach((doc) => {
          documents.push(docToData<T>(doc));
        });
        onDocumentsReceived(documents);
      },
      (error) => {
        if (onError) {
          onError(error);
        } else {
          logError(error as Error, "Error listening to documents");
        }
      },
    );

    return unsubscribe;
  }

  static async addDocument<T extends { [x: string]: any }>(
    collectionPath: string,
    data: T,
  ): Promise<T> {
    try {
      const docRef = await addDoc(collection(getDB(), collectionPath), data);
      return { id: docRef.id, ...data };
    } catch (error) {
      logError(error as Error, "Error adding document");
      throw error;
    }
  }

  static async addDocumentWithId<T extends { id?: string; [x: string]: any }>(
    collectionPath: string,
    data: T,
  ): Promise<T> {
    try {
      const docRef = data.id
        ? doc(collection(getDB(), collectionPath), data.id)
        : doc(collection(getDB(), collectionPath));

      await setDoc(docRef, data);

      return { id: docRef.id, ...data };
    } catch (error) {
      logError(error as Error, "Error adding document");
      throw error;
    }
  }

  static async addDocumentWithId2<T extends { id?: string; [x: string]: any }>(
    collectionPath: string,
    data: T,
  ): Promise<T> {
    try {
      const docRef = doc(collection(getDB(), collectionPath));
      data["id"] = docRef.id;
      await addDoc(collection(getDB(), collectionPath), data);
      return { id: docRef.id, ...data };
    } catch (error) {
      throw error;
    }
  }

  static async updateDocument(
    collectionPath: string,
    docId: string,
    fieldsToUpdate: Record<string, any>,
  ): Promise<void> {
    try {
      const docRef = doc(getDB(), collectionPath, docId);
      await updateDoc(docRef, fieldsToUpdate);
    } catch (error) {
      logError(error as Error, "Error updating document");
      throw error;
    }
  }

  static async deleteDocument(
    collectionPath: string,
    docId: string,
  ): Promise<void> {
    try {
      const docRef = doc(getDB(), collectionPath, docId);
      await deleteDoc(docRef);
    } catch (error) {
      logError(error as Error, "Error deleting document");
      throw error;
    }
  }

  static listenToDocument<T>(
    collectionPath: string,
    documentId: string,
    onDocumentReceived: (document: T | null) => void,
    onError?: (error: FirestoreError) => void,
  ): () => void {
    const documentRef = doc(getDB(), collectionPath, documentId);

    const unsubscribe = onSnapshot(
      documentRef,
      (documentSnapshot) => {
        if (documentSnapshot.exists()) {
          const documentData = docToData<T>(documentSnapshot);
          onDocumentReceived(documentData);
        } else {
          onDocumentReceived(null);
        }
      },
      (error) => {
        if (onError) {
          onError(error);
        } else {
          logError(error as Error, "Error listening to document");
        }
      },
    );

    return unsubscribe;
  }

  static listenToQueryCollection<T>(
    collectionPath: string,
    onCollectionUpdate: (documents: T[]) => void,
    onError?: (error: FirestoreError) => void,
    ...queryConstraints: QueryConstraint[]
  ): () => void {
    const queryCollection = query(
      collection(getDB(), collectionPath),
      ...queryConstraints,
    );
    const unsubscribe = onSnapshot(
      queryCollection,
      (querySnapshot) => {
        let documents: T[] = [];
        querySnapshot.forEach((doc) => {
          documents.push(docToData<T>(doc));
        });
        onCollectionUpdate(documents);
      },
      (error) => {
        if (onError) {
          onError(error);
        } else {
          logError(error as Error, "Error listening to collection");
        }
      },
    );
    return unsubscribe;
  }

  static listenToQueryCollectionForFirstBatch<T>(
    collectionPath: string,
    limitingValue: number,
    onCollectionUpdate: (documents: T[]) => void,
    onError?: (error: FirestoreError) => void,
    ...queryConstraints: QueryConstraint[]
  ): () => void {
    const initialQuery = query(
      collection(getDB(), collectionPath),
      ...queryConstraints,
      limit(limitingValue),
    );

    const unsubscribe = onSnapshot(
      initialQuery,
      (querySnapshot) => {
        let documents: T[] = [];
        querySnapshot.forEach((doc) => {
          documents.push(docToData<T>(doc));
        });
        onCollectionUpdate(documents);
      },
      (error) => {
        logError(error as Error, "Error listening to collection");
        if (onError) {
          onError(error);
        } else {
          logError(error as Error, "Error listening to collection");
        }
      },
    );
    return unsubscribe;
  }

  static async fetchQueryCollection<T>(
    collectionPath: string,
    onCollectionUpdate: (documents: T[]) => void,
    onError?: (error: FirestoreError) => void,
    ...queryConstraints: QueryConstraint[]
  ): Promise<void> {
    const queryCollection = query(
      collection(getDB(), collectionPath),
      ...queryConstraints,
    );

    try {
      const querySnapshot = await getDocs(queryCollection);
      let documents: T[] = [];
      querySnapshot.forEach((doc) => {
        documents.push(docToData<T>(doc));
      });
      onCollectionUpdate(documents);
    } catch (error) {
      if (onError) {
        onError(error as FirestoreError);
      } else {
        logError(error as Error, "Error fetching collection");
      }
    }
  }

  static async getAllDocumentCount(
    collectionPath: string,
    ...queryConstraints: QueryConstraint[]
  ): Promise<number> {
    try {
      const collectionQuery = query(
        collection(getDB(), collectionPath),
        ...queryConstraints,
      );

      const snapShot = await getCountFromServer(collectionQuery);
      return snapShot.data().count;
    } catch (error) {
      logError(error as Error, "Error fetching document count");
      throw error;
    }
  }

  static async getAllDocumentCountByCompositeFilter(
    collectionPath: string,
    compositeFilter: QueryCompositeFilterConstraint,
    extraConstraints: QueryConstraint[] = [],
  ): Promise<number> {
    try {
      const constraints: QueryConstraint[] = [
        compositeFilter as unknown as QueryConstraint,
        ...extraConstraints,
      ];

      const collectionQuery = query(
        collection(getDB(), collectionPath),
        ...constraints,
      );

      const snapShot = await getCountFromServer(collectionQuery);
      return snapShot.data().count;
    } catch (error) {
      logError(
        error as Error,
        "Error fetching document count with composite filter",
      );
      throw error;
    }
  }

  static async addHumanEvidencesOn(
    caseId: string,
    data: Checklist[],
    checklistId: string,
  ): Promise<void> {
    try {
      if (!data || data.length === 0) {
        logError(new Error("No data provided to add to the checklist."));
        throw new Error();
      }

      const checklistValueModelObject: Checklist = data[0];
      const docPath = `/orders/${caseId}/filledChecklist/${checklistId}`;
      const docRef = doc(getDB(), docPath);
      await setDoc(docRef, checklistValueModelObject, { merge: true });
    } catch (error) {
      logError(error as Error, "Error adding human evidences to checklist");
      throw new Error(
        `Failed to add human evidences to case ID: ${caseId}, checklist ID: ${checklistId}. ${
          error instanceof Error ? error.message : ""
        }`,
      );
    }
  }

  static async addCPTDetailsOnOrder(
    orderId: string,
    cptCode: string,
    cptDescription: string,
  ): Promise<void> {
    const docRef = doc(getDB(), "orders", orderId);
    try {
      await setDoc(
        docRef,
        {
          Orders: arrayUnion({
            Procedure: {
              Code: cptCode,
              Description: cptDescription,
            },
            IsDeletable: true,
            PriorAuthNeeded: true,
          }),
        },
        { merge: true },
      );
    } catch (error) {
      logError(error as Error, "Error adding CPT details to order");
    }
  }

  static async deleteCPTDetailsFromOrder(
    orderId: string,
    cptCode: string,
    cptDescription: string,
  ): Promise<void> {
    const docRef = doc(getDB(), "orders", orderId);
    try {
      await setDoc(
        docRef,
        {
          Orders: arrayRemove({
            Procedure: {
              Code: cptCode,
              Description: cptDescription,
            },
            IsDeletable: true,
            PriorAuthNeeded: true,
          }),
        },
        { merge: true },
      );
    } catch (error) {
      logError(error as Error, "Error removing CPT details from order");
    }
  }

  static async updateVisitTypeForOrder(
    orderId: string,
    visitType: string,
    visitDescription: string,
  ): Promise<void> {
    const docRef = doc(getDB(), "orders", orderId);
    try {
      await updateDoc(docRef, {
        "Visit.Type.Type": visitType,
        "Visit.Type.Description": visitDescription,
      });
    } catch (error) {
      logError(error as Error, "Error updating visit type");
    }
  }

  static async setDocument(
    collectionPath: string,
    docId: string,
    data: Record<string, any>,
  ): Promise<void> {
    try {
      const docRef = doc(getDB(), collectionPath, docId);
      await setDoc(docRef, data);
    } catch (error) {
      throw error;
    }
  }
}

export async function inviteNewMember(memberData: MemberModel): Promise<void> {
  try {
    await FirestoreService.addDocument(
      "/healthcare_facility_invitation",
      memberData,
    );
  } catch (error) {
    logError(error as Error, "Error inviting new member");
    throw new Error(
      `Failed to invite new member. ${
        error instanceof Error ? error.message : "Unknown error occurred."
      }`,
    );
  }
}

export const getFileDownloadUrl = async (filePath: string): Promise<string> => {
  try {
    const storage = getStorage(getFirebaseApp());
    const fileRef = ref(storage, filePath);
    const downloadUrl = await getDownloadURL(fileRef);
    return downloadUrl;
  } catch (error) {
    logError(error as Error, "Error fetching the download URL");
    return "";
  }
};

export const updateFormDetailsForCmm = async (
  orderId: string,
  formData: Record<string, any>,
): Promise<void> => {
  const docRef = doc(
    getDB(),
    FirestoreCollectionReference.oncoEmrOrders(),
    orderId,
  );

  try {
    await updateDoc(docRef, {
      "form.form_data": formData,
    });
  } catch (error) {
    logError(error as Error, "Error updating form details");
    throw new Error();
  }
};

export const updateOrCreateDiffDocumentForCmmWithId = async (
  orderId: string,
  docId: string,
  data: Record<string, any>,
  modifiedBy: string,
): Promise<void> => {
  const docRef = doc(
    getDB(),
    FirestoreCollectionReference.cmmFormDiffData(orderId),
    docId,
  );

  try {
    await updateDoc(docRef, {
      data: data,
      modifiedBy: modifiedBy,
      modifiedAt: Timestamp.now(),
    });
  } catch (error) {
    const fireStoreError = error as FirestoreError;
    if (fireStoreError.code.includes("not-found")) {
      try {
        await setDoc(docRef, {
          data: data,
          modifiedBy: modifiedBy,
          modifiedAt: Timestamp.now(),
        });
      } catch (createError) {
        logError(createError as Error, "Error creating document");
      }
    } else {
      logError(error as Error, "Error updating document");
    }
  }
};

export const getFileFromFirebaseStorage = async (
  filePath: string,
): Promise<File> => {
  try {
    const downloadURL = await getFileDownloadUrl(filePath);
    const response = await fetch(downloadURL);
    const blob = await response.blob();
    logDataToConsole("Fetched file from Firebase Storage", blob);
    return new File([blob], filePath);
  } catch (error) {
    logError(error as Error, "Could not download the file");
    throw new Error("Error downloading document from url");
  }
};

export const updateMedicalPaOrder = async (
  orderId: string,
  data: Record<string, any>,
): Promise<void> => {
  try {
    await FirestoreService.updateDocument(
      FirestoreCollectionReference.medicalPaOrders(),
      orderId,
      data,
    );
  } catch (error) {
    logError(error as Error, "Error updating medical pa order");
    throw new Error();
  }
};

export const updateEvBvStatus = async (
  orderId: string,
  status: string,
): Promise<void> => {
  const docRef = doc(
    getDB(),
    FirestoreCollectionReference.medicalPaOrders(),
    orderId,
  );
  try {
    await updateDoc(docRef, {
      "status.financial_review": status,
      "status_history.evbv_at": new Date().toISOString(),
    });
  } catch (error) {
    logError(error as Error, "Error updating evbv status");
    throw new Error();
  }
};
export const addCmmEvent = async (
  orderId: string,
  event: CmmEvent,
): Promise<void> => {
  try {
    await FirestoreService.addDocument(
      FirestoreCollectionReference.cmmEvents(orderId),
      event,
    );
  } catch (error) {
    logError(error as Error, "Error adding CMM event");
  }
};

export async function batchUpdateDocuments(
  updates: {
    collectionRef: ReturnType<typeof doc>["parent"];
    docId: string;
    data: Record<string, any>;
  }[],
): Promise<void> {
  const batch = writeBatch(getDB());

  for (const { collectionRef, docId, data } of updates) {
    const docRef = doc(collectionRef, docId);
    batch.update(docRef, data);
  }

  await batch.commit();
}

/**
 * Batch operation type for atomic writes
 */
export type BatchOperation =
  | {
      type: "set";
      collectionPath: string;
      docId: string;
      data: Record<string, any>;
    }
  | {
      type: "update";
      collectionPath: string;
      docId: string;
      data: Record<string, any>;
    };

/**
 * Create-only operation used when related documents must share one atomic commit.
 */
export interface BatchCreateOperation {
  collectionPath: string;
  docId: string;
  data: Record<string, unknown>;
}

/**
 * Creates multiple documents atomically after verifying that none already exist.
 *
 * @param operations - Documents to create in one transaction
 * @returns Promise resolved after all documents are committed
 * @throws Error when any target document already exists or Firestore rejects the transaction
 */
export async function batchCreateDocuments(
  operations: BatchCreateOperation[],
): Promise<void> {
  const database = getDB();
  const documentRefs = operations.map((operation) =>
    doc(database, operation.collectionPath, operation.docId),
  );

  await runTransaction(database, async (transaction) => {
    const snapshots = await Promise.all(
      documentRefs.map((documentRef) => transaction.get(documentRef)),
    );
    const conflictingIndex = snapshots.findIndex((snapshot) =>
      snapshot.exists(),
    );
    if (conflictingIndex >= 0) {
      const conflict = operations[conflictingIndex];
      throw new Error(
        `Document already exists: ${conflict.collectionPath}/${conflict.docId}`,
      );
    }

    operations.forEach((operation, index) => {
      transaction.set(documentRefs[index], operation.data);
    });
  });
}

/**
 * Performs multiple Firestore operations atomically using batch writes.
 * Supports both 'set' (create/overwrite) and 'update' operations.
 * All operations succeed or all fail together.
 *
 * @param operations - Array of batch operations to perform
 * @throws Error if the batch commit fails
 *
 * @example
 * await batchWriteDocuments([
 *   { type: "set", collectionPath: "orders/123/comments", docId: "comment-1", data: { text: "Hello" } },
 *   { type: "update", collectionPath: "orders", docId: "123", data: { updated_at: "..." } },
 * ]);
 */
export async function batchWriteDocuments(
  operations: BatchOperation[],
): Promise<void> {
  const batch = writeBatch(getDB());

  for (const operation of operations) {
    const docRef = doc(getDB(), operation.collectionPath, operation.docId);

    if (operation.type === "set") {
      batch.set(docRef, operation.data);
    } else {
      batch.update(docRef, operation.data);
    }
  }

  await batch.commit();
}

export const writeOtpErrorToFirebase = async (
  email: string,
  error: string,
): Promise<void> => {
  await FirestoreService.addDocument(FirestoreCollectionReference.otpErrors(), {
    email: email,
    error: error,
    created_at: Timestamp.now(),
  });
};

export const recordWritebackQcToFirebase = async (
  record: WritebackQcRecord,
): Promise<void> => {
  try {
    await FirestoreService.addDocument(
      FirestoreCollectionReference.writebackQc(record.order_id),
      {
        order_id: record.order_id,
        org_id: record.org_id,
        user_id: record.user_id,
        user_email: record.user_email,
        timestamp: record.timestamp,
        issues: record.issues,
      },
    );
  } catch (error) {
    logError(error as Error, "Error recording writeback QC to Firebase");
  }
};

export const deleteFileFromFirebaseStorage = async (
  filePath: string,
): Promise<void> => {
  try {
    const storage = getStorage(getFirebaseApp());
    const fileRef = ref(storage, filePath);
    await deleteObject(fileRef);
  } catch (error) {
    logError(error as Error, "Error deleting file from Firebase Storage");
    throw new Error(
      `Failed to delete file from Firebase Storage at ${filePath}: ${(error as Error).message}`,
    );
  }
};

/**
 * Fetches and formats query comments for a medical PA order.
 *
 * Retrieves the medical PA order document from Firestore and extracts the query comments.
 * If multiple comments exist, they are joined with commas into a single string.
 *
 * @param orderId - The unique identifier of the medical PA order
 * @returns A formatted string of query comments, or "No comments to display" if none exist or on error
 *
 * @example
 * const comments = await getQueryCommentsForMedicalPaOrder("order-123");
 * // Returns: "Comment 1, Comment 2, Comment 3" or "No comments to display"
 */
export const getQueryCommentsForMedicalPaOrder = async (
  orderId: string,
): Promise<string> => {
  try {
    const doc: MedicalPaOrder = await FirestoreService.getDocument(
      FirestoreDocumentReference.medicalPaOrders(orderId),
    );

    const queryComments = doc?.comments?.query_comments;

    // If it's an array, join non-empty values
    if (Array.isArray(queryComments)) {
      const validComments = queryComments
        .map((comment) => comment?.comment?.trim())
        .filter(Boolean);
      return validComments.length > 0
        ? validComments.join(", ")
        : "No comments to display";
    }

    return "No comments to display";
  } catch (error) {
    console.error("Error fetching query comments:", error);
    return "No comments to display";
  }
};

export const listenToNchPortalQuestionnaire = (
  orderId: string,
  onData: (questions: MedicalQuestion[]) => void,
  onError: (error: Error) => void,
): (() => void) => {
  const fullPath = FirestoreDocumentReference.nchPortalQuestionnaire(orderId);
  const collectionPath = fullPath.substring(0, fullPath.lastIndexOf("/"));
  const documentId = fullPath.substring(fullPath.lastIndexOf("/") + 1);

  return FirestoreService.listenToDocument<{
    question_tree: MedicalQuestion[];
  }>(
    collectionPath,
    documentId,
    (doc) => {
      onData(doc?.question_tree ?? []);
    },
    (error) => {
      logError(error as Error, "Error listening to questionnaire");
      onError(error);
    },
  );
};
